/**
 * RegisterPage -- Standalone account creation page for browser context.
 *
 * Rendered under PlatformLayout at /register. Reuses useRegister hook
 * from @/lib/auth. The register API auto-logs-in on success (it returns a
 * token + loggedIn=true when email verification is not enforced, or returns
 * success=true with loggedIn=false when verification is required — the
 * AuthCard shows the same "check your email" confirmation in that case).
 *
 * After successful registration:
 *  - loggedIn=true  → navigate to /account (auto-login path)
 *  - loggedIn=false → show "check your email" confirmation (verify-first path)
 *
 * Links:
 *  - "Already have an account? Sign in" → /login
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, Check, Loader2, Mail } from 'lucide-react'
import { useRegister } from '@/lib/auth'
import { Button } from '@/shared/components/forms/Button'
import { Input } from '@/shared/components/forms/Input'
import { OAuthButtons } from './OAuthButtons'

export function RegisterPage() {
  const navigate = useNavigate()
  const registerMutation = useRegister()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [verifyEmailSent, setVerifyEmailSent] = useState(false)

  const isPending = registerMutation.isPending
  const hasError = registerMutation.data && !registerMutation.data.success

  const passwordMismatch = confirmPassword.length > 0 && password !== confirmPassword
  const passwordTooShort = password.length > 0 && password.length < 8

  const canSubmit =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    password.length >= 8 &&
    password === confirmPassword &&
    !isPending

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    const result = await registerMutation.mutateAsync({
      name: name.trim(),
      email: email.trim(),
      password,
    })

    if (result.success) {
      if (result.data?.loggedIn) {
        // Auto-login path: session is live, go straight to account
        navigate('/account', { replace: true })
      } else {
        // Email-verification-required path: show confirmation
        setVerifyEmailSent(true)
      }
    }
  }

  // Email verification confirmation state
  if (verifyEmailSent) {
    return (
      <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="rounded-xl bg-ue-bg-dark/80 border border-ue-border-medium/60 p-6">
            <div className="p-3 bg-ue-accent-green/10 border border-ue-accent-green/20 rounded text-ue-accent-green text-sm mb-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Check className="w-4 h-4 shrink-0" />
                <span className="font-semibold">Account created!</span>
              </div>
              <p className="text-ue-accent-green/80 text-xs">
                Check your email for a verification link, then sign in below.
              </p>
            </div>
            <Link to="/login">
              <Button size="md" className="w-full">
                Go to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="rounded-xl bg-ue-bg-dark/80 border border-ue-border-medium/60 p-6">
          <h1 className="text-lg font-semibold text-ue-text-primary mb-1">
            Create your account
          </h1>
          <p className="text-xs text-ue-text-secondary mb-5">
            Free to join. Unlock your modules after purchase.
          </p>

          {/* Server error banner */}
          {hasError && (
            <div className="p-2.5 mb-4 bg-ue-accent-red/10 border border-ue-accent-red/20 rounded text-ue-accent-red text-xs flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {registerMutation.data?.error || 'Registration failed. Please try again.'}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              type="text"
              label="Full name"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isPending}
              autoComplete="name"
              autoFocus
            />
            <Input
              type="email"
              label="Email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isPending}
              autoComplete="email"
            />
            <Input
              type="password"
              label="Password"
              placeholder="Min. 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isPending}
              autoComplete="new-password"
              error={passwordTooShort ? 'Password must be at least 8 characters' : undefined}
            />
            <Input
              type="password"
              label="Confirm password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isPending}
              autoComplete="new-password"
              error={passwordMismatch ? 'Passwords do not match' : undefined}
            />

            <Button
              type="submit"
              size="lg"
              loading={isPending}
              disabled={!canSubmit}
              className="w-full mt-1"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Creating account…
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Social sign-in — renders only for providers the backend has
              configured; the "or" divider only appears when at least one is
              enabled (OAuthButtons returns null otherwise). */}
          <OAuthButtons showDivider />
        </div>

        {/* Sign in link below card */}
        <p className="mt-4 text-center text-xs text-ue-text-tertiary">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-ue-accent-primary hover:text-ue-accent-primary/80 font-medium transition-colors"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
